import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import GeneralContext from "../GeneralContext/GeneralContext";
import axios from "../../api/axios";
import dayjs from "dayjs";
import {
  GraficoCobertura,
  GraficoComposicion,
  GraficoDescuentos,
  GraficoEnviosExtra,
  GraficoIntegridadPorMetrica,
  GraficoMapaCalor,
  GraficoRecepcionPorOperador,
  GraficoRecepcionPorRbd,
  GraficoSerieDiaria,
  GraficoVelocidadGlobal,
  GraficoVelocidadPorOperador,
} from "./ReporteCalidadGraficos";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const VELOCIDADES = ["bajada", "subida", "interac_bajada"];

function periodosDisponibles() {
  const base = dayjs().startOf("month");
  return Array.from({ length: 36 }, (_, i) =>
    base.subtract(i, "month").format("YYYY-MM-01")
  );
}

function etiquetaPeriodo(periodo) {
  if (!periodo) return "—";
  const d = dayjs(periodo);
  return `${MESES[d.month()]} ${d.year()}`;
}

function numero(valor, decimales = 0) {
  if (valor == null) return "—";
  return Number(valor).toLocaleString("es-CL", {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
}

function porcentaje(valor) {
  if (valor == null) return "—";
  return `${numero(valor, 2)}%`;
}

function nombreOperador(fila) {
  return fila?.nombre_operador || `Operador ${fila?.id_operador}`;
}

async function describirError(e) {
  const data = e?.response?.data;
  let detalle = null;
  try {
    if (data instanceof Blob) {
      detalle = JSON.parse(await data.text())?.detail;
    } else if (data) {
      detalle = data.detail;
    }
  } catch {
    detalle = null;
  }
  if (detalle) return detalle;
  if (e?.response?.status === 403) return "No tienes privilegios para ver este reporte.";
  return "No se pudo generar el reporte. Intenta nuevamente.";
}

function Seccion({ titulo, nota, children }) {
  return (
    <Paper elevation={0} sx={{ border: "1px solid rgba(0,0,0,0.15)", mb: 2.5 }}>
      <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>
      </Box>
      {children}
      {nota && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
            {nota}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function Tabla({ cabeceras, alineaciones = [], filas }) {
  if (!filas.length) {
    return (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
          Sin datos para este corte.
        </Typography>
      </Box>
    );
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {cabeceras.map((c, i) => (
              <TableCell
                key={c}
                sx={{ fontWeight: 600 }}
                align={alineaciones[i] || "left"}
              >
                {c}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filas.map((fila, idx) => (
            <TableRow key={idx} hover>
              {fila.map((valor, i) => (
                <TableCell key={i} align={alineaciones[i] || "left"}>
                  {valor}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TablaCriticos({ filas, encabezado, formato }) {
  return (
    <Tabla
      cabeceras={["RBD", "Operador", "Zona", "Exigibles", "Reportados",
        "Recepción", encabezado]}
      alineaciones={["right", "left", "left", "right", "right", "right", "right"]}
      filas={filas.map((f) => [
        numero(f.rbd),
        f.nombre_operador ?? "—",
        f.nombre_zona ?? "—",
        numero(f.total),
        numero(f.reportados),
        porcentaje(f.porcentaje_recepcion),
        formato(f),
      ])}
    />
  );
}

export default function ReporteCalidad() {
  const { setPageTitle } = useContext(GeneralContext);

  const periodos = useMemo(() => periodosDisponibles(), []);
  const [periodo, setPeriodo] = useState(periodos[1] ?? periodos[0]);
  const [idOperador, setIdOperador] = useState("");
  const [ambito, setAmbito] = useState("ventana");

  const [operadores, setOperadores] = useState([]);
  const [reporte, setReporte] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPageTitle?.("REPORTE DE CALIDAD DE DATOS");
  }, []);

  useEffect(() => {
    axios
      .getAllOperadores()
      .then((res) => setOperadores(res?.data ?? []))
      .catch(() => setOperadores([]));
  }, []);

  const parametros = () => ({
    periodo,
    id_operador: idOperador || undefined,
    ambito,
  });

  const consultar = async () => {
    setCargando(true);
    setError(null);
    setReporte(null);
    try {
      const res = await axios.getReporteCalidad(parametros());
      setReporte(res?.data ?? null);
    } catch (e) {
      setError(await describirError(e));
    } finally {
      setCargando(false);
    }
  };

  const descargarPDF = async () => {
    setDescargando(true);
    setError(null);
    try {
      const res = await axios.getReporteCalidadPDF(parametros());
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      const sufijo = idOperador ? `_operador_${idOperador}` : "";
      link.setAttribute("download", `reporte_calidad_${periodo}${sufijo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(await describirError(e));
    } finally {
      setDescargando(false);
    }
  };

  const resumen = reporte?.resumen;
  const cobertura = reporte?.cobertura;
  const velocidad = reporte?.velocidad;
  const criticos = reporte?.criticos;

  return (
    <PageBody>
      <Box width="100%">
        <Typography variant="h1" sx={{ textAlign: "center", p: "0.6rem" }}>
          REPORTE DE CALIDAD DE DATOS
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", mb: 3 }}>
          Recepción, integridad y conformidad de los datos de supervisión del
          período, sobre la malla de slots de 15 minutos.
        </Typography>

        <Box
          sx={{
            mb: 3,
            p: 2.5,
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 1,
            background: "#fff",
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: "0 0 200px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Período:
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
            >
              {periodos.map((p) => (
                <MenuItem key={p} value={p}>
                  {etiquetaPeriodo(p)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ flex: "0 0 280px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Operador:
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={idOperador}
              onChange={(e) => setIdOperador(e.target.value)}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Todos los operadores</MenuItem>
              {operadores.map((o) => (
                <MenuItem key={o.id_operador} value={o.id_operador}>
                  {o.nombre_operador}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ flex: "0 0 250px" }}>
            <Typography variant="body1" sx={{ mb: 0.25, fontWeight: 500 }}>
              Ámbito:
            </Typography>
            <RadioGroup
              row
              value={ambito}
              onChange={(e) => setAmbito(e.target.value)}
            >
              <FormControlLabel
                value="ventana"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Ventana</Typography>}
              />
              <FormControlLabel
                value="dia"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Día completo</Typography>}
              />
            </RadioGroup>
          </Box>

          <Box sx={{ alignSelf: "flex-end", display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={
                cargando ? <CircularProgress size={16} /> : <AssessmentOutlinedIcon />
              }
              disabled={cargando}
              onClick={consultar}
              sx={{
                background: "#C8D8F0",
                color: "#1565c0",
                boxShadow: "none",
                textTransform: "none",
                "&:hover": { background: "#b3c8e8", boxShadow: "none" },
              }}
            >
              {cargando ? "Calculando..." : "Ver reporte"}
            </Button>
            <Button
              startIcon={descargando ? <CircularProgress size={16} /> : <DownloadIcon />}
              disabled={cargando || descargando}
              onClick={descargarPDF}
              sx={{ textTransform: "none" }}
            >
              {descargando ? "Generando..." : "Descargar PDF"}
            </Button>
          </Box>
        </Box>

        {cargando && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "30vh",
            }}
          >
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Calculando métricas del período...</Typography>
          </Box>
        )}

        {!cargando && error && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              mb: 3,
              border: "1px solid rgba(198, 40, 40, 0.3)",
              background: "rgba(198, 40, 40, 0.08)",
            }}
          >
            <ErrorOutlineIcon sx={{ color: "#C62828" }} fontSize="small" />
            <Typography variant="body2" sx={{ color: "#C62828" }}>
              {error}
            </Typography>
          </Paper>
        )}

        {!cargando && reporte?.aviso && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              mb: 3,
              border: "1px solid rgba(237, 108, 2, 0.3)",
              background: "rgba(237, 108, 2, 0.08)",
            }}
          >
            <ErrorOutlineIcon sx={{ color: "#ED6C02" }} fontSize="small" />
            <Typography variant="body2" sx={{ color: "#B35300" }}>
              {reporte.aviso}
            </Typography>
          </Paper>
        )}

        {!cargando && reporte && resumen && (
          <PageBgBlock pad="0">
            <Seccion
              titulo={`Resumen — ${etiquetaPeriodo(reporte.periodo)}${
                reporte.nombre_operador ? ` — ${reporte.nombre_operador}` : ""
              } — ámbito ${reporte.nombre_ambito}`}
              nota="Recepción: slots reportados sobre exigibles. Integridad: slots correctos sobre reportados. Dato conforme: slots correctos sobre exigibles. Un slot es correcto si llegó, es numéricamente válido y está dentro de los parámetros de la norma."
            >
              <Tabla
                cabeceras={["Indicador", "Valor"]}
                alineaciones={["left", "right"]}
                filas={[
                  ["Establecimientos analizados", numero(resumen.rbd)],
                  ["Operadores involucrados", numero(resumen.operadores)],
                  ["Zonas involucradas", numero(resumen.zonas)],
                  ["Días con datos calculados", numero(resumen.dias_con_datos)],
                  ["Slots exigibles", numero(resumen.total)],
                  ["Slots reportados", numero(resumen.reportados)],
                  ["Mediciones recibidas", numero(resumen.recibidos)],
                  ["Mediciones de más", numero(resumen.extra)],
                  ["Slots no recibidos", numero(resumen.faltantes)],
                  ["Slots inválidos", numero(resumen.invalidos)],
                  ["Slots anómalos", numero(resumen.anomalos)],
                  ["Slots correctos", numero(resumen.correctos)],
                  ["Recepción", porcentaje(resumen.porcentaje_recepcion)],
                  ["Integridad", porcentaje(resumen.porcentaje_integridad)],
                  ["Dato conforme", porcentaje(resumen.porcentaje_conforme)],
                  ["Establecimientos sin ningún reporte", numero(resumen.rbd_sin_reporte)],
                  [
                    `Establecimientos bajo ${numero(resumen.umbral_recepcion)}% de recepción`,
                    numero(resumen.rbd_bajo_umbral),
                  ],
                ]}
              />
              <GraficoSerieDiaria reporte={reporte} />
            </Seccion>

            {cobertura && (
              <Seccion
                titulo="Cobertura del cálculo"
                nota="Compara los RBD-día presentes en stats_rbd_diario contra los esperados (parque activo por cada día del mes). Un período calculado a medias baja todos los totales sin que eso signifique que el operador reportó menos. Los faltantes se completan desde Admin Area → Estadísticas RBD."
              >
                <Tabla
                  cabeceras={["Indicador", "Valor"]}
                  alineaciones={["left", "right"]}
                  filas={[
                    ["Días del período", numero(cobertura.dias_periodo)],
                    ["Establecimientos en el parque activo", numero(cobertura.rbd_parque)],
                    ["RBD-día esperados", numero(cobertura.rbd_dia_esperados)],
                    ["RBD-día calculados", numero(cobertura.rbd_dia_calculados)],
                    ["Cobertura del cálculo", porcentaje(cobertura.porcentaje)],
                    [
                      "Días sin ningún RBD calculado",
                      numero((cobertura.dias_sin_calculo ?? []).length),
                    ],
                  ]}
                />
                <GraficoCobertura reporte={reporte} />
              </Seccion>
            )}

            <Seccion
              titulo="Recepción por operador"
              nota="La distribución por establecimiento muestra si el promedio del operador esconde un grupo de colegios sin servicio de datos. Caja: primer a tercer cuartil, línea en la mediana, bigotes de Tukey y los establecimientos fuera de norma como puntos."
            >
              <Tabla
                cabeceras={["Operador", "RBD", "Sin reporte", "Exigibles", "Reportados",
                  "No recib.", "Recepción", "Integridad", "Conforme"]}
                alineaciones={["left", "right", "right", "right", "right", "right",
                  "right", "right", "right"]}
                filas={(reporte.por_operador ?? []).map((f) => [
                  nombreOperador(f),
                  numero(f.rbd),
                  numero(f.rbd_sin_reporte),
                  numero(f.total),
                  numero(f.reportados),
                  numero(f.faltantes),
                  porcentaje(f.porcentaje_recepcion),
                  porcentaje(f.porcentaje_integridad),
                  porcentaje(f.porcentaje_conforme),
                ])}
              />
              <GraficoRecepcionPorOperador reporte={reporte} />
              <GraficoRecepcionPorRbd reporte={reporte} />
            </Seccion>

            <Seccion
              titulo="Integridad del dato recibido"
              nota="Un slot inválido es un dato que el operador no pudo medir; uno anómalo es una medición que está fuera del parámetro comprometido. Una métrica sin dato válido no siempre implica corte: puede ser un campo que el equipo del establecimiento no reporta."
            >
              <GraficoComposicion reporte={reporte} />
              <Tabla
                cabeceras={["Métrica", "Sin dato válido", "Válidas", "% sin dato"]}
                alineaciones={["left", "right", "right", "right"]}
                filas={(reporte.integridad?.por_metrica ?? []).map((f) => [
                  f.nombre,
                  numero(f.sentinela),
                  numero(f.validos),
                  porcentaje(f.porcentaje_sentinela),
                ])}
              />
              <GraficoIntegridadPorMetrica reporte={reporte} />
              {(reporte.integridad?.por_operador ?? []).length > 1 && (
                <Tabla
                  cabeceras={["Operador", "Latencia", "Bajada", "Subida",
                    "Bajada int.", "Pérdida"]}
                  alineaciones={["left", "right", "right", "right", "right", "right"]}
                  filas={reporte.integridad.por_operador.map((f) => [
                    nombreOperador(f),
                    porcentaje(f.latencia),
                    porcentaje(f.bajada),
                    porcentaje(f.subida),
                    porcentaje(f.interac_bajada),
                    porcentaje(f.perdida_paquetes),
                  ])}
                />
              )}
            </Seccion>

            {(reporte.envios_extra?.por_operador ?? []).some((f) => f.extra > 0) && (
              <Seccion
                titulo="Mediciones enviadas de más"
                nota="Lo esperado es una medición por establecimiento cada 15 minutos. Las mediciones que llegan para un slot ya cubierto no mejoran la recepción y son indicio de reenvío, doble reporte o un equipo emitiendo fuera de cadencia."
              >
                <Tabla
                  cabeceras={["Operador", "RBD", "Con envíos de más", "Reportados",
                    "Recibidas", "De más", "% de más"]}
                  alineaciones={["left", "right", "right", "right", "right",
                    "right", "right"]}
                  filas={reporte.envios_extra.por_operador.map((f) => [
                    nombreOperador(f),
                    numero(f.rbd),
                    numero(f.rbd_con_extra),
                    numero(f.reportados),
                    numero(f.recibidos),
                    numero(f.extra),
                    porcentaje(f.porcentaje_extra),
                  ])}
                />
                <GraficoEnviosExtra reporte={reporte} />
                {(reporte.envios_extra?.detalle ?? []).length > 0 && (
                  <Tabla
                    cabeceras={["RBD", "Operador", "Zona", "Reportados", "Recibidas",
                      "De más", "%"]}
                    alineaciones={["right", "left", "left", "right", "right",
                      "right", "right"]}
                    filas={reporte.envios_extra.detalle.map((f) => [
                      numero(f.rbd),
                      f.nombre_operador ?? "—",
                      f.nombre_zona ?? "—",
                      numero(f.reportados),
                      numero(f.recibidos),
                      numero(f.extra),
                      porcentaje(f.porcentaje_extra),
                    ])}
                  />
                )}
              </Seccion>
            )}

            {velocidad && VELOCIDADES.some((m) => velocidad.global?.[m]?.rbd_medidos) && (
              <Seccion
                titulo="Velocidad entregada frente a la contratada"
                nota="La mediana medida de cada establecimiento sobre su velocidad contratada. Cien por ciento es entregar exactamente lo contratado. Se calcula sobre la mediana del período, no sobre el peak: un enlace que cumple la mitad del tiempo queda igual bajo el cien por ciento. Los establecimientos sin velocidad contratada registrada quedan fuera del corte."
              >
                <Tabla
                  cabeceras={["Velocidad", "RBD medidos", "Bajo contrato",
                    "% bajo contrato", "Mediana", "Q1", "Q3"]}
                  alineaciones={["left", "right", "right", "right", "right",
                    "right", "right"]}
                  filas={VELOCIDADES.filter((m) => velocidad.global?.[m]).map((m) => {
                    const corte = velocidad.global[m];
                    return [
                      corte.nombre,
                      numero(corte.rbd_medidos),
                      numero(corte.rbd_bajo_contrato),
                      porcentaje(corte.porcentaje_bajo_contrato),
                      porcentaje(corte.ratio?.mediana),
                      porcentaje(corte.ratio?.q1),
                      porcentaje(corte.ratio?.q3),
                    ];
                  })}
                />
                <GraficoVelocidadGlobal reporte={reporte} />
                {(velocidad.por_operador ?? []).length > 1 && (
                  <Tabla
                    cabeceras={["Operador", "RBD medidos", "Bajo contrato",
                      "% bajo contrato", "Mediana", "Q1", "Q3"]}
                    alineaciones={["left", "right", "right", "right", "right",
                      "right", "right"]}
                    filas={velocidad.por_operador.map((f) => [
                      nombreOperador(f),
                      numero(f.bajada?.rbd_medidos),
                      numero(f.bajada?.rbd_bajo_contrato),
                      porcentaje(f.bajada?.porcentaje_bajo_contrato),
                      porcentaje(f.bajada?.ratio?.mediana),
                      porcentaje(f.bajada?.ratio?.q1),
                      porcentaje(f.bajada?.ratio?.q3),
                    ])}
                  />
                )}
                <GraficoVelocidadPorOperador reporte={reporte} />
              </Seccion>
            )}

            <Seccion
              titulo="Causales de descuento"
              nota="Calculadas con las mismas reglas de la prefactura pero sin descontar justificaciones de indisponibilidad: sirven para comparar y detectar desvíos, no para facturar. Dd tiene prioridad: un slot completamente caído no se cuenta además como latencia o pérdida fuera de norma."
            >
              <Tabla
                cabeceras={["Causal", "Slots", "% de la ventana"]}
                alineaciones={["left", "right", "right"]}
                filas={(reporte.descuentos?.global ?? []).map((f) => [
                  f.nombre,
                  numero(f.slots),
                  porcentaje(f.porcentaje),
                ])}
              />
              {(reporte.descuentos?.por_operador ?? []).length > 1 && (
                <Tabla
                  cabeceras={["Operador", "Slots ventana", "Dprop", "Dd", "Dl", "Dp"]}
                  alineaciones={["left", "right", "right", "right", "right", "right"]}
                  filas={reporte.descuentos.por_operador.map((f) => [
                    nombreOperador(f),
                    numero(f.slots_ventana),
                    porcentaje(f.perc_Dprop),
                    porcentaje(f.perc_Dd),
                    porcentaje(f.perc_Dl),
                    porcentaje(f.perc_Dp),
                  ])}
                />
              )}
              <GraficoDescuentos reporte={reporte} />
            </Seccion>

            {(reporte.mapa_calor?.filas ?? []).length > 0 && (
              <Seccion
                titulo="Mapa de no recepción"
                nota="Porcentaje de slots que no llegaron, por día del mes. Las celdas en blanco son días sin ninguna falta. Distingue una falla puntual de una degradación sostenida."
              >
                <GraficoMapaCalor reporte={reporte} />
              </Seccion>
            )}

            {(reporte.por_zona ?? []).length > 1 && (
              <Seccion titulo="Por zona">
                <Tabla
                  cabeceras={["Zona", "RBD", "Exigibles", "Reportados", "Recepción",
                    "Integridad", "Conforme"]}
                  alineaciones={["left", "right", "right", "right", "right",
                    "right", "right"]}
                  filas={reporte.por_zona.map((f) => [
                    f.nombre_zona ?? f.id_zona ?? "—",
                    numero(f.rbd),
                    numero(f.total),
                    numero(f.reportados),
                    porcentaje(f.porcentaje_recepcion),
                    porcentaje(f.porcentaje_integridad),
                    porcentaje(f.porcentaje_conforme),
                  ])}
                />
              </Seccion>
            )}

            {(reporte.por_tecnologia ?? []).length > 1 && (
              <Seccion titulo="Por tecnología">
                <Tabla
                  cabeceras={["Tecnología", "RBD", "Exigibles", "Reportados",
                    "Recepción", "Integridad", "Conforme"]}
                  alineaciones={["left", "right", "right", "right", "right",
                    "right", "right"]}
                  filas={reporte.por_tecnologia.map((f) => [
                    f.nombre_tecnologia ?? f.tipo_tecnologia ?? "—",
                    numero(f.rbd),
                    numero(f.total),
                    numero(f.reportados),
                    porcentaje(f.porcentaje_recepcion),
                    porcentaje(f.porcentaje_integridad),
                    porcentaje(f.porcentaje_conforme),
                  ])}
                />
              </Seccion>
            )}

            {criticos && (
              <>
                {(criticos.por_faltantes ?? []).length > 0 && (
                  <Seccion
                    titulo="Establecimientos con más slots no recibidos"
                    nota="La lista sobre la que se puede actuar: cada fila identifica el establecimiento, su operador y su zona."
                  >
                    <TablaCriticos
                      filas={criticos.por_faltantes}
                      encabezado="No recibidos"
                      formato={(f) => numero(f.valor)}
                    />
                  </Seccion>
                )}

                {(criticos.por_anomalos ?? []).length > 0 && (
                  <Seccion titulo="Establecimientos con más slots anómalos">
                    <TablaCriticos
                      filas={criticos.por_anomalos}
                      encabezado="Anómalos"
                      formato={(f) => numero(f.valor)}
                    />
                  </Seccion>
                )}

                {(criticos.por_invalidos ?? []).length > 0 && (
                  <Seccion titulo="Establecimientos con más slots inválidos">
                    <TablaCriticos
                      filas={criticos.por_invalidos}
                      encabezado="Inválidos"
                      formato={(f) => numero(f.valor)}
                    />
                  </Seccion>
                )}

                {(criticos.por_velocidad ?? []).length > 0 && (
                  <Seccion
                    titulo="Establecimientos con menor velocidad de bajada frente a la contratada"
                    nota="Ordenados por la mediana medida sobre la contratada. Cien por ciento es entregar lo comprometido."
                  >
                    <TablaCriticos
                      filas={criticos.por_velocidad}
                      encabezado="% contratado"
                      formato={(f) => porcentaje(f.ratio_bajada)}
                    />
                  </Seccion>
                )}
              </>
            )}
          </PageBgBlock>
        )}
      </Box>
    </PageBody>
  );
}
